import { Route, Navigate } from "react-router-dom";
import { landingFor, isAdminRole } from './helpers';
import ProtectedRoute from '../../components/ProtectedRoute';
import InsuranceFeatureGate from '../../features/insurance/components/InsuranceFeatureGate';
import lazyWithRetry from '../../utils/lazyWithRetry';
import {
  RedirectPayments,
  RedirectInvoices,
  RedirectInsurance,
  RedirectPayerPayments,
  RedirectAdminBilling,
  RedirectClaimDetail,
  RedirectBillingEraDetail,
  RedirectWithSearch,
} from '../../pages/billing/BillingRedirects';
import {
  BillingEstimatesLayout,
  BillingInsuranceLayout,
  BillingReportsLayout,
} from '../../pages/billing/BillingDomainLayouts';

// Lazy-loaded pages
const SuperbillsPage = lazyWithRetry(() => import('../../pages/SuperbillsPage'));
const SuperbillDetailPage = lazyWithRetry(() => import('../../pages/SuperbillDetailPage'));
const InvoiceListView = lazyWithRetry(() => import('../../features/billing/InvoiceListView'));
const GFEListView = lazyWithRetry(() => import('../../features/billing/GFEListView'));
const PaymentDashboard = lazyWithRetry(() => import('../../features/payment-dashboard/PaymentDashboard'));
const BillingHomePage = lazyWithRetry(() => import('../../pages/billing/BillingHomePage'));
const BillingLayout = lazyWithRetry(() => import('../../pages/billing/BillingLayout'));
const InsuranceDashboard = lazyWithRetry(() => import('../../pages/insurance/InsuranceDashboard'));
const ClaimDetailPage = lazyWithRetry(() => import('../../pages/insurance/ClaimDetailPage'));
const RemittanceListPage = lazyWithRetry(() => import('../../pages/billing/RemittanceListPage'));
const RemittanceDetailPage = lazyWithRetry(() => import('../../pages/billing/RemittanceDetailPage'));
const PayerPaymentsPage = lazyWithRetry(() => import('../../pages/admin/PayerPaymentsPage'));
const ProviderRevenueView = lazyWithRetry(() => import('../../features/billing/ProviderRevenueView'));
const AccountsReceivableView = lazyWithRetry(() => import('../../features/billing/AccountsReceivableView'));
const ReconciliationView = lazyWithRetry(() => import('../../features/billing/ReconciliationView'));
const DisputeLossReviewPage = lazyWithRetry(() => import('../../pages/billing/DisputeLossReviewPage'));

/** Billing Routes - Routes for billing, superbills, invoices, insurance */
export function BillingRoutes({ user, userHasRole, isClientUser = (_user: unknown) => true }) {
  return (
    <>
      {/* ─── Unified Billing Layout with sub-nav ─── */}
      <Route path="/billing" element={<ProtectedRoute requiresPermission="billing.view"><BillingLayout /></ProtectedRoute>}>
        <Route index element={<BillingHomePage />} />
        <Route path="needs-attention" element={<RedirectWithSearch to="/billing" />} />
        <Route path="work-queue" element={<RedirectWithSearch to="/billing" />} />
        <Route path="clients" element={<AccountsReceivableView />} />
        <Route path="payments" element={<PaymentDashboard />} />
        <Route path="invoices" element={<RedirectWithSearch to="/billing/invoices-statements" />} />
        <Route path="invoices-statements" element={<InvoiceListView />} />
        <Route path="estimates" element={<BillingEstimatesLayout />}>
          <Route index element={<GFEListView />} />
          <Route path="superbills" element={<RedirectWithSearch to="/billing/superbills" />} />
        </Route>
        <Route path="superbills" element={<SuperbillsPage />} />
        <Route path="insurance" element={<InsuranceFeatureGate><BillingInsuranceLayout /></InsuranceFeatureGate>}>
          <Route index element={<InsuranceDashboard />} />
          <Route path="claims/:claimId" element={<ClaimDetailPage />} />
          <Route path="era" element={<RemittanceListPage />} />
          <Route path="era/:batchId" element={<RemittanceDetailPage />} />
          <Route path="payer-payments" element={<PayerPaymentsPage />} />
          <Route path="exceptions" element={<RemittanceListPage key="exceptions" defaultFilter="needs_review" />} />
        </Route>
        <Route path="reports" element={<BillingReportsLayout />}>
          <Route index element={<ProviderRevenueView />} />
          <Route path="revenue" element={<ProviderRevenueView />} />
          <Route path="reconciliation" element={<ReconciliationView startDate={undefined} endDate={undefined} />} />
        </Route>
        <Route
          path="dispute-loss-reviews"
          element={
            <ProtectedRoute allowedRoles={['clinical_director']} requiresPermission="billing.view">
              <ProtectedRoute requiresPermission="payroll.manage">
                <DisputeLossReviewPage />
              </ProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route path="ar" element={<RedirectWithSearch to="/billing/clients" />} />

        <Route path="era" element={<RedirectWithSearch to="/billing/insurance/era" />} />
        <Route path="era/:batchId" element={<RedirectBillingEraDetail />} />
        <Route path="gfe" element={<RedirectWithSearch to="/billing/estimates" />} />
        <Route path="payer-payments" element={<RedirectWithSearch to="/billing/insurance/payer-payments" />} />
        <Route path="revenue" element={<RedirectWithSearch to="/billing/reports/revenue" />} />
        <Route path="reconciliation" element={<RedirectWithSearch to="/billing/reports/reconciliation" />} />
      </Route>

      {/* ─── Backward-compatible redirects (preserve query params) ─── */}
      <Route path="/payments" element={<RedirectPayments />} />
      <Route path="/invoices" element={<RedirectInvoices />} />
      <Route path="/insurance" element={<RedirectInsurance />} />
      <Route path="/insurance/claims/:claimId" element={<RedirectClaimDetail />} />
      <Route path="/admin/payer-payments" element={<RedirectPayerPayments />} />
      <Route path="/admin/billing" element={<RedirectAdminBilling />} />
      <Route path="/billing/revenue-report" element={<RedirectWithSearch to="/billing/revenue" />} />

      {/* Superbills: standalone route preserves old role-based access for providers
          who may not have billing.view (it's a Billing Tier Add-On) */}
      <Route
        path="/superbills"
        element={
          user === null
            ? <div>Checking login...</div>
            : user && (userHasRole(user, 'provider') || isAdminRole(user) || userHasRole(user, 'supervisor'))
            ? <SuperbillsPage />
            : isClientUser(user) ? <Navigate to="/client" /> : <Navigate to={landingFor(user)} />
        }
      />
      <Route
        path="/superbills/:id"
        element={
          user === null
            ? <div>Checking login...</div>
            : user && (userHasRole(user, 'provider') || isAdminRole(user) || userHasRole(user, 'supervisor'))
            ? <SuperbillDetailPage />
            : isClientUser(user) ? <Navigate to="/client" /> : <Navigate to={landingFor(user)} />
        }
      />
    </>
  );
}
